import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useContent, resolveAsset } from "@/context/ContentContext";
import ShareButton from "@/components/ShareButton";

const ease = [0.16, 1, 0.3, 1];

export default function BrutalDesign() {
  const { content } = useContent() || {};
  const b = content?.brutal || {};
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [errored, setErrored] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v || errored) return;
    if (v.paused) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => setErrored(true));
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      id="brutal-design"
      data-testid="brutal-design-section"
      className="relative py-24 md:py-40 bg-[#050505] overflow-hidden"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-14">
          <div className="overline">
            <span className="accent-bar" />
            {b.kicker || "03 — Featured Project"}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="overline text-[#c01515]">{b.release_tag || "● Coming to OTT · 2027"}</div>
            <ShareButton slug="brutal-design" label="Share" testId="share-brutal" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease }}
            className="md:col-span-12 mb-2"
          >
            <h2
              className="font-display font-light leading-[0.85] tracking-[-0.04em]"
              style={{ fontSize: "clamp(4rem, 17vw, 18rem)" }}
            >
              {b.title_top || "BRUTAL"}
              <br />
              <span className="italic text-[#c01515]">{b.title_bottom || "DESIGN"}</span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease, delay: 0.1 }}
            className="md:col-span-7 mt-6"
          >
            <div className="relative img-frame aspect-[16/10] crosshair group">
              <video
                ref={videoRef}
                data-testid="brutal-trailer-video"
                src={resolveAsset(b.trailer || "/assets/brutal-trailer.mp4")}
                poster={resolveAsset(b.poster || "/assets/brutal-poster.jpg")}
                playsInline
                preload="none"
                onPause={() => setPlaying(false)}
                onPlay={() => setPlaying(true)}
                onEnded={() => setPlaying(false)}
                onError={() => { setErrored(true); setPlaying(false); }}
                className="w-full h-full object-cover"
                style={{ filter: "grayscale(15%) contrast(1.05)" }}
              />
              {!playing && !errored && (
                <button
                  data-testid="brutal-play-btn"
                  onClick={togglePlay}
                  className="absolute inset-0 flex items-center justify-center bg-[#050505]/35 transition-all hover:bg-[#050505]/15"
                  aria-label="Play BRUTAL DESIGN trailer"
                >
                  <span className="w-20 h-20 md:w-28 md:h-28 rounded-full border border-white/40 backdrop-blur-md bg-white/5 flex items-center justify-center transition-all hover:scale-110 hover:bg-[#c01515] hover:border-[#c01515]">
                    <svg width="22" height="26" viewBox="0 0 22 26" fill="white">
                      <path d="M0 0 L22 13 L0 26 Z" />
                    </svg>
                  </span>
                </button>
              )}
              {errored && (
                <div data-testid="brutal-error-note" className="absolute inset-0 flex items-center justify-center bg-[#050505]/40">
                  <div className="text-center">
                    <p className="overline mb-3">Trailer unavailable in this browser</p>
                    <p className="font-display italic text-xl text-white/70">The full reel will arrive with the OTT release.</p>
                  </div>
                </div>
              )}
              {playing && (
                <button data-testid="brutal-pause-btn" onClick={togglePlay}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full border border-white/30 bg-black/50 backdrop-blur-md flex items-center justify-center hover:bg-white hover:text-black transition-all">
                  <span className="overline text-[10px]">‖</span>
                </button>
              )}
              <div className="absolute top-4 left-4 overline text-[#c01515] z-10">● TRAILER</div>
              <div className="absolute bottom-4 right-4 overline z-10">Tap to play</div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="overline">{b.trailer_caption || "Reel 01 · Pre-release"}</span>
              <span className="overline">{b.trailer_year_label || "Brutal Design — OTT MMXXVII"}</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease, delay: 0.25 }}
            className="md:col-span-5 mt-6 flex flex-col justify-between"
          >
            <div>
              <p className="overline mb-4">Synopsis</p>
              <p className="font-display text-2xl md:text-3xl leading-snug text-[#f5f5f5]/90">
                {b.synopsis_lead || "A lonely and rich psychologist's dark world suddenly unravels when"}{" "}
                <span className="text-[#c01515] italic">{b.synopsis_emphasis || "five of her patients"}</span>{" "}
                {b.synopsis_tail || "become involved in a series of suicide and accident cases."}
              </p>
            </div>

            <div className="mt-10 grid grid-cols-2 gap-6 border-t border-white/10 pt-8">
              <div><p className="overline">Genre</p><p className="mt-2 font-display text-xl">{b.genre || "Psychological Thriller"}</p></div>
              <div><p className="overline">Format</p><p className="mt-2 font-display text-xl">{b.format || "OTT Limited Series"}</p></div>
              <div><p className="overline">Created by</p><p className="mt-2 font-display text-xl">{b.creator || "Ranjana Baruah"}</p></div>
              <div><p className="overline">Status</p><p className="mt-2 font-display text-xl">{b.status || "In Development"}</p></div>
            </div>

            {b.external_url && (
              <a
                href={b.external_url}
                target="_blank"
                rel="noreferrer"
                data-testid="brutal-external-link"
                className="mt-8 group inline-flex items-center justify-between gap-4 border border-white/15 hover:border-[#c01515] px-5 py-4 transition-all"
              >
                <span>
                  <span className="overline block mb-1 text-[#a3a3a3] group-hover:text-[#c01515] transition-colors">In the project</span>
                  <span className="font-display text-xl md:text-2xl text-[#f5f5f5]">
                    {b.external_label || "Visit brutallifestyle.com"}
                  </span>
                </span>
                <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#a3a3a3] group-hover:text-[#c01515] transition-colors">
                  Open →
                </span>
              </a>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.1, ease, delay: 0.35 }}
            className="md:col-span-5 md:col-start-8 mt-6"
          >
            <div className="img-frame aspect-[4/5]">
              <img src={resolveAsset(b.poster || "/assets/brutal-poster.jpg")} alt="BRUTAL DESIGN — official still" loading="lazy" />
            </div>
            <span className="overline mt-3 inline-block">Still 02 · The Tableau</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
